import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { LayoutDashboard, Calendar, KanbanSquare, Wallet, StickyNote as NoteIcon,
  Bell, Users, Settings, Search, Sun, Moon, LogOut, Shield, ChevronDown, ChevronLeft,
  Repeat, Share2, Menu as MenuIcon, SlidersHorizontal, ArrowUp, ArrowDown, RotateCcw,
  MonitorPlay, BookOpen, Mail } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { Brand } from '@/components/Brand';
import api from '@/lib/api';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetClose } from '@/components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';

export const NAV = [
  { to: '/plannerapp', icon: LayoutDashboard, label: 'Dashboard', end: true, locked: true },
  { to: '/plannerapp/calendar', icon: Calendar, label: 'Calendario' },
  { to: '/plannerapp/tasks', icon: KanbanSquare, label: 'Tareas & Proyectos' },
  { to: '/plannerapp/finance', icon: Wallet, label: 'Finanzas' },
  { to: '/plannerapp/notes', icon: NoteIcon, label: 'Notas IA' },
  { to: '/plannerapp/emails', icon: Mail, label: 'Correos' },
  { to: '/plannerapp/books', icon: BookOpen, label: 'Libros Consultoría' },
  { to: '/plannerapp/plannerspace', icon: MonitorPlay, label: 'PlannerSpace' },
  { to: '/plannerapp/routines', icon: Repeat, label: 'Rutinas' },
  { to: '/plannerapp/network', icon: Share2, label: 'Red / Cuentas', ownerOnly: true },
  { to: '/plannerapp/assistants', icon: Users, label: 'Asistentes & Auditoría', ownerOnly: true },
  { to: '/plannerapp/settings', icon: Settings, label: 'Ajustes', locked: true },
];

// Orden + visibilidad de menú POR USUARIO (users.menu_prefs)
export const useOrderedNav = () => {
  const { user } = useAuth();
  const base = NAV.filter((n) => !n.ownerOnly || user?.role === 'owner');
  const prefs = user?.menu_prefs || {};
  const savedOrder = Array.isArray(prefs.order) ? prefs.order : [];
  const hidden = Array.isArray(prefs.hidden) ? prefs.hidden : [];
  const byTo = Object.fromEntries(base.map((n) => [n.to, n]));
  const ordered = [...savedOrder.filter((to) => byTo[to]), ...base.filter((n) => !savedOrder.includes(n.to))].map((x) => (typeof x === 'string' ? byTo[x] : x));
  const visible = ordered.filter((n) => n.locked || !hidden.includes(n.to));
  return { all: base, ordered, visible, hidden };
};

export const MenuCustomizer = ({ trigger }) => {
  const { user, updateProfile } = useAuth();
  const { ordered, hidden } = useOrderedNav();
  const [open, setOpen] = useState(false);
  const save = (nextOrder, nextHidden) => updateProfile({ menu_prefs: { order: nextOrder.map((n) => n.to), hidden: nextHidden } }).catch(() => {});
  const move = (idx, dir) => {
    const arr = [...ordered]; const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    save(arr, hidden);
  };
  const toggle = (n) => { if (n.locked) return; const nx = hidden.includes(n.to) ? hidden.filter((x) => x !== n.to) : [...hidden, n.to]; save(ordered, nx); };
  const reset = () => updateProfile({ menu_prefs: {} }).catch(() => {});
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="pp-glass-strong" data-testid="menu-customizer">
        <DialogHeader><DialogTitle>Personaliza tu menú</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground -mt-2">Oculta o reordena módulos. Es privado de tu usuario y puedes revertirlo.</p>
        <div className="space-y-1 mt-3 max-h-[55vh] overflow-y-auto pp-scroll">
          {ordered.map((n, i) => (
            <div key={n.to} className="flex items-center gap-2 py-2 px-2 rounded-xl hover:bg-[rgba(255,255,255,0.03)]">
              <n.icon size={16} className="pp-mint shrink-0" />
              <span className="text-sm flex-1 truncate">{n.label}</span>
              <button onClick={() => move(i, -1)} disabled={i === 0} className="w-7 h-7 grid place-items-center rounded-lg border disabled:opacity-30" data-testid={`menu-up-${n.label.split(' ')[0].toLowerCase()}`}><ArrowUp size={13} /></button>
              <button onClick={() => move(i, 1)} disabled={i === ordered.length - 1} className="w-7 h-7 grid place-items-center rounded-lg border disabled:opacity-30" data-testid={`menu-down-${n.label.split(' ')[0].toLowerCase()}`}><ArrowDown size={13} /></button>
              <Switch checked={n.locked || !hidden.includes(n.to)} disabled={n.locked} onCheckedChange={() => toggle(n)} data-testid={`menu-toggle-${n.label.split(' ')[0].toLowerCase()}`} />
            </div>
          ))}
        </div>
        <button data-testid="reset-menu-btn" onClick={reset} className="mt-2 h-10 rounded-xl border text-sm flex items-center justify-center gap-2"><RotateCcw size={15} /> Restaurar menú original</button>
      </DialogContent>
    </Dialog>
  );
};

const linkClass = ({ isActive }) => `group flex items-center gap-3 h-11 px-3 rounded-xl transition-colors ${isActive ? 'pp-mint bg-[rgba(62,180,137,0.14)] pp-glow-mint font-semibold' : 'text-muted-foreground hover:text-foreground hover:bg-[rgba(120,160,145,0.12)]'}`;

export const Sidebar = () => {
  const { user } = useAuth();
  const { visible } = useOrderedNav();
  return (
    <aside data-testid="app-sidebar" className="hidden md:flex fixed left-0 top-0 bottom-0 z-40 w-[248px] flex-col py-5 px-3 pp-glass-strong border-r">
      <div className="px-2 mb-6"><Brand size={40} /></div>
      <nav className="flex-1 space-y-1 overflow-y-auto pp-scroll">
        {visible.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} data-testid={`nav-${n.label.split(' ')[0].toLowerCase()}`} className={linkClass}>
            <n.icon size={19} className="shrink-0" />
            <span className="text-sm truncate">{n.label}</span>
          </NavLink>
        ))}
        {user?.role === 'super_master' && (
          <NavLink to="/plannerapp/admin" data-testid="nav-admin"
            className={({ isActive }) => `flex items-center gap-3 h-11 px-3 rounded-xl ${isActive ? 'pp-gold pp-glow-gold font-semibold' : 'text-muted-foreground hover:text-foreground'}`}>
            <Shield size={19} /> <span className="text-sm">Super Master</span>
          </NavLink>
        )}
      </nav>
      <div className="pt-3 border-t">
        <MenuCustomizer trigger={
          <button data-testid="customize-menu-desktop" className="w-full flex items-center gap-2 h-9 px-3 rounded-xl text-xs text-muted-foreground hover:text-foreground hover:bg-[rgba(120,160,145,0.12)]">
            <SlidersHorizontal size={14} /> Personalizar menú
          </button>} />
        <div className="px-3 pt-2 text-[10px] text-muted-foreground">PlannerPRO · v2</div>
      </div>
    </aside>
  );
};

export const TopBar = ({ onSearch }) => {
  const { user, logout, activeAccount, setActiveAccountId } = useAuth();
  const { theme, toggle } = useTheme();
  const [count, setCount] = useState(0);
  const [accounts, setAccounts] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isDashboard = location.pathname === '/plannerapp' || location.pathname === '/plannerapp/';

  useEffect(() => {
    if (!searchOpen) return;
    if (q.trim().length < 2) { setResults([]); return; }
    setSearching(true);
    const t = setTimeout(async () => {
      try { const { data } = await api.get(`/search?q=${encodeURIComponent(q)}`); setResults(data.results || []); } catch { setResults([]); }
      setSearching(false);
    }, 280);
    return () => clearTimeout(t);
  }, [q, searchOpen]);

  useEffect(() => {
    const onKey = (e) => { if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setSearchOpen(true); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const goResult = (r) => { setSearchOpen(false); setQ(''); navigate(r.route); };

  const loadNotifs = async () => {
    try { const { data } = await api.get('/notifications'); setCount(data.filter((n) => !n.read).length); } catch (err) { console.error('No se pudieron cargar las notificaciones:', err); }
  };
  useEffect(() => {
    loadNotifs();
    const iv = setInterval(loadNotifs, 15000);
    if (user?.role === 'assistant') api.get('/auth/my-accounts').then(({ data }) => setAccounts(data)).catch(() => {});
    return () => clearInterval(iv);
  }, [user, activeAccount]);

  const activeName = accounts.find((a) => a.account_id === activeAccount)?.owner_name;
  const goBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/plannerapp');
  };

  return (
    <header data-testid="topbar" className="fixed top-0 right-0 left-0 md:left-[248px] z-40 h-16 flex items-center gap-2 sm:gap-3 px-3 sm:px-6 pp-glass-strong border-b">
      {/* Botón Volver global */}
      <button data-testid="back-button" onClick={goBack} disabled={isDashboard}
        className={`w-10 h-10 rounded-xl grid place-items-center shrink-0 transition-colors ${isDashboard ? 'opacity-30 cursor-default' : 'hover:bg-[rgba(255,255,255,0.06)] pp-mint'}`}
        title="Volver" aria-label="Volver">
        <ChevronLeft size={20} />
      </button>
      <div className="md:hidden"><Brand size={30} showText={false} /></div>
      <button data-testid="global-search-input" onClick={() => setSearchOpen(true)} className="flex-1 max-w-md flex items-center gap-2 h-10 px-3 rounded-xl bg-[rgba(255,255,255,0.06)] border text-muted-foreground text-sm hover:bg-[rgba(255,255,255,0.1)] transition-colors min-w-0">
        <Search size={16} className="shrink-0" /> <span className="truncate">Buscar en todo...</span>
        <kbd className="ml-auto text-[10px] px-1.5 py-0.5 rounded bg-[rgba(255,255,255,0.08)] hidden sm:inline">⌘K</kbd>
      </button>
      <div className="flex-1 hidden sm:block" />

      <Dialog open={searchOpen} onOpenChange={setSearchOpen}>
        <DialogContent className="pp-glass-strong max-w-xl top-[15%] translate-y-0" data-testid="global-search-dialog">
          <div className="flex items-center gap-2 border-b border-[rgba(255,255,255,0.08)] pb-3">
            <Search size={18} className="pp-mint" />
            <input autoFocus data-testid="global-search-field" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Busca tareas, notas, finanzas, libros, reuniones..." className="flex-1 bg-transparent outline-none text-foreground" />
          </div>
          <div className="max-h-[55vh] overflow-y-auto pp-scroll mt-2 -mx-2">
            {q.trim().length < 2 && <div className="text-sm text-muted-foreground px-4 py-6 text-center">Escribe al menos 2 letras.</div>}
            {q.trim().length >= 2 && !searching && results.length === 0 && <div className="text-sm text-muted-foreground px-4 py-6 text-center">Sin resultados para "{q}".</div>}
            {results.map((r, i) => (
              <button key={`${r.type}-${r.route || r.id || i}`} data-testid={`search-result-${i}`} onClick={() => goResult(r)} className="w-full text-left px-4 py-2.5 rounded-xl hover:bg-[rgba(62,180,137,0.1)] flex items-center gap-3">
                <span className="text-[10px] px-2 py-0.5 rounded-full pp-mint bg-[rgba(62,180,137,0.12)] shrink-0">{r.type}</span>
                <span className="flex-1 min-w-0"><span className="text-sm block truncate">{r.title}</span>{r.subtitle && <span className="text-[11px] text-muted-foreground block truncate">{r.subtitle}</span>}</span>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {user?.role === 'assistant' && accounts.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="topbar-account-switcher" className="flex items-center gap-2 h-10 px-3 rounded-xl bg-[rgba(255,255,255,0.06)] border text-sm hover:bg-[rgba(255,255,255,0.1)]">
              <Users size={15} className="pp-mint" /> <span className="hidden sm:inline">{activeName || 'Cuenta'}</span> <ChevronDown size={14} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="pp-glass-strong">
            <DropdownMenuLabel>Cambiar de cuenta</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {accounts.map((a) => (
              <DropdownMenuItem key={a.account_id} data-testid={`switch-account-${a.account_id}`} onClick={() => { setActiveAccountId(a.account_id); window.location.reload(); }}>
                <Avatar className="w-6 h-6 mr-2">{a.avatar && <AvatarImage src={a.avatar} />}<AvatarFallback className="text-[10px] bg-[rgba(62,180,137,0.2)]">{a.owner_name?.[0]}</AvatarFallback></Avatar>
                {a.owner_name}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      <ConnectivityLight />
      <button data-testid="quick-logout" onClick={logout} title="Cerrar sesión" className="w-10 h-10 rounded-xl grid place-items-center hover:bg-[rgba(227,93,106,0.12)] text-red-400 shrink-0">
        <LogOut size={18} />
      </button>

      <button data-testid="topbar-notifications-button" onClick={() => navigate('/plannerapp/notifications')} className="relative w-10 h-10 rounded-xl grid place-items-center hover:bg-[rgba(255,255,255,0.06)] shrink-0">
        <Bell size={19} />
        {count > 0 && (
          <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }} className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold grid place-items-center text-white" style={{ background: '#C9A84C' }}>{count}</motion.span>
        )}
      </button>

      <button data-testid="theme-toggle-switch" onClick={toggle} className="w-10 h-10 rounded-xl grid place-items-center hover:bg-[rgba(255,255,255,0.06)] shrink-0">
        <AnimatePresence mode="wait">
          {theme === 'dark' ? <motion.span key="m" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }}><Moon size={18} className="pp-mint" /></motion.span>
            : <motion.span key="s" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }}><Sun size={18} className="pp-gold" /></motion.span>}
        </AnimatePresence>
      </button>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button data-testid="topbar-avatar" className="flex items-center gap-2 h-10 pl-1 pr-1 sm:pr-3 rounded-xl hover:bg-[rgba(255,255,255,0.06)] shrink-0">
            <Avatar className="w-9 h-9">{user?.avatar && <AvatarImage src={user.avatar} alt={user?.name} />}<AvatarFallback className="bg-[rgba(62,180,137,0.2)] pp-mint font-semibold">{user?.name?.[0]?.toUpperCase()}</AvatarFallback></Avatar>
            {user?.role !== 'super_master' && (
              <span data-testid="topbar-plan-badge" className="hidden sm:inline text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full border border-[rgba(201,168,76,0.5)] pp-gold">
                {({ free: 'Starter', pro: 'Pro', business: 'Business' }[user?.plan]) || user?.plan || 'Starter'}
              </span>
            )}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="pp-glass-strong w-56">
          <DropdownMenuLabel><div className="font-semibold">{user?.name}</div><div className="text-xs text-muted-foreground font-normal">{user?.email}</div><div className="text-[10px] pp-mint uppercase tracking-wide mt-1">{user?.role?.replace('_', ' ')}</div></DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => navigate('/plannerapp/settings')}><Settings size={15} className="mr-2" /> Ajustes</DropdownMenuItem>
          <DropdownMenuItem data-testid="logout-button" onClick={logout} className="text-red-400"><LogOut size={15} className="mr-2" /> Cerrar sesión</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
};

// Indicador de conexión: PlannerLIVE (encendido) / PlannerOFF (apagado).
// Al reconectar, refresca los datos automáticamente.
export const ConnectivityLight = () => {
  const [online, setOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  useEffect(() => {
    const goOnline = () => {
      setOnline(true);
      // Auto-actualización al reconectar
      setTimeout(() => { window.location.reload(); }, 600);
    };
    const goOffline = () => setOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => { window.removeEventListener('online', goOnline); window.removeEventListener('offline', goOffline); };
  }, []);
  return (
    <div data-testid="connectivity-light" title={online ? 'En línea' : 'Sin conexión'}
      className="hidden sm:flex items-center gap-2 h-10 px-3 rounded-xl border shrink-0"
      style={{ borderColor: online ? 'rgba(62,180,137,0.4)' : 'rgba(227,93,106,0.4)', background: online ? 'rgba(62,180,137,0.10)' : 'rgba(227,93,106,0.08)' }}>
      <motion.span
        animate={online ? { boxShadow: ['0 0 4px rgba(62,180,137,0.6)', '0 0 12px rgba(62,180,137,0.9)', '0 0 4px rgba(62,180,137,0.6)'] } : {}}
        transition={{ duration: 1.8, repeat: Infinity }}
        className="w-2.5 h-2.5 rounded-full"
        style={{ background: online ? '#3EB489' : '#6b3a3f' }} />
      <span className="text-[11px] font-semibold tracking-wide" style={{ color: online ? '#3EB489' : '#E35D6A' }}>
        {online ? 'PlannerLIVE' : 'PlannerOFF'}
      </span>
    </div>
  );
};


export const BottomBar = () => {
  const { user } = useAuth();
  const { visible } = useOrderedNav();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const primary = visible.slice(0, 4);
  return (
    <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 h-16 flex items-center justify-around px-2 pp-glass-strong border-t">
      {primary.map((n) => (
        <NavLink key={n.to} to={n.to} end={n.end} data-testid={`bottomnav-${n.label.split(' ')[0].toLowerCase()}`}
          className={({ isActive }) => `flex flex-col items-center gap-0.5 px-2 py-1 rounded-xl ${isActive ? 'pp-mint' : 'text-muted-foreground'}`}>
          <n.icon size={20} /><span className="text-[9px]">{n.label.split(' ')[0]}</span>
        </NavLink>
      ))}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>
          <button data-testid="bottomnav-more" className="flex flex-col items-center gap-0.5 px-2 py-1 rounded-xl text-muted-foreground">
            <MenuIcon size={20} /><span className="text-[9px]">Más</span>
          </button>
        </SheetTrigger>
        <SheetContent side="bottom" className="pp-glass-strong border-t rounded-t-3xl max-h-[80vh] overflow-y-auto pp-scroll">
          <SheetHeader className="mb-3 flex-row items-center justify-between">
            <SheetTitle className="text-left">Menú completo</SheetTitle>
            <MenuCustomizer trigger={
              <button data-testid="customize-menu-mobile" className="flex items-center gap-1.5 text-xs pp-mint border rounded-lg px-2.5 h-8"><SlidersHorizontal size={13} /> Personalizar</button>} />
          </SheetHeader>
          <div className="grid grid-cols-3 gap-3 pb-6">
            {visible.map((n) => (
              <button key={n.to} data-testid={`moremenu-${n.label.split(' ')[0].toLowerCase()}`}
                onClick={() => { navigate(n.to); setOpen(false); }}
                className="flex flex-col items-center justify-center gap-2 h-24 rounded-2xl pp-glass hover:pp-glow-mint transition-shadow">
                <n.icon size={24} className="pp-mint" />
                <span className="text-[11px] text-center leading-tight px-1">{n.label}</span>
              </button>
            ))}
            {user?.role === 'super_master' && (
              <button onClick={() => { navigate('/plannerapp/admin'); setOpen(false); }} className="flex flex-col items-center justify-center gap-2 h-24 rounded-2xl pp-glass">
                <Shield size={24} className="pp-gold" /><span className="text-[11px]">Super Master</span>
              </button>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </nav>
  );
};
